import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Space, Spin, Typography } from 'antd'
import { filter, isEmpty, isNil, join, map, size } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  MetadataTreeErrorsModal,
  ReduxMetadataTree,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
  ROOT_METADATA_NODE_ID,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { LOCATION_TYPE_NODE_TYPES } from '../../constants'
import { renderLocationTypeEvent } from '../../helpers'
import {
  useDeleteLocationTypeCategoryMutation,
  useDeleteLocationTypeMutation,
  useLocationTypesAndLocationTypeCategoriesQuery,
  useReplaceLocationTypeMutation,
  useRevertReplaceLocationTypeMutation,
  useSaveLocationTypesAndLocationTypeCategoriesMutation,
  useSetIsCurationEnabledLocationTypeOrLocationTypeCategoryMutation,
} from '../../hooks'
import {
  addLocationTypeOrLocationTypeCategoryNode,
  expandLocationTypeAndLocationTypeCategoryMainTreeKeys,
  expandLocationTypeAndLocationTypeCategorySupportingTreeKeys,
  moveLocationTypes,
  renameLocationTypeOrLocationTypeCategoryNode,
  resetLocationTypesAndLocationTypeCategoriesTree,
  setDataForRenamingLocationTypeOrLocationTypeCategory,
} from '../../redux/actions'

const LocationTypesPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle('Location Types')
  const [isMoveActive, setIsMoveActive] = useState(false)
  const [isReplaceActive, setIsReplaceActive] = useState(false)

  const eventEntityTypes = useMemo(
    () => [
      EVENT_METADATA_TYPES.LOCATION_TYPE_CATEGORY,
      EVENT_METADATA_TYPES.LOCATION_TYPE,
    ],
    [],
  )

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useLocationTypesAndLocationTypeCategoriesQuery()

  const [
    saveLocationTypesAndLocationTypeCategories,
    { isLoading: isSaveLoading, error: saveError },
  ] = useSaveLocationTypesAndLocationTypeCategoriesMutation(refetchLastEvent)

  const [deleteLocationTypeCategory] =
    useDeleteLocationTypeCategoryMutation(refetchLastEvent)

  const [deleteLocationType] = useDeleteLocationTypeMutation(refetchLastEvent)

  const [setIsCurationEnabledLocationTypeOrLocationTypeCategory] =
    useSetIsCurationEnabledLocationTypeOrLocationTypeCategoryMutation(
      refetchLastEvent,
    )

  const [replaceLocationType] = useReplaceLocationTypeMutation(refetchLastEvent)

  const [revertReplaceLocationType] =
    useRevertReplaceLocationTypeMutation(refetchLastEvent)

  const {
    isDirty,
    data,
    dataForRenaming,
    mainTreeExpandedKeys,
    supportingTreeExpandedKeys,
  } = useSelector(
    state => state.locationTypes.locationTypesAndLocationTypeCategories,
  )

  const hasFullPermission = useUserHasPermissions([
    PERMISSIONS.METADATA_TREE.SAVE,
  ])

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(
    node => {
      const isLocationTypeCategory = isNil(node.parentId)

      const nodeToCreate = isLocationTypeCategory
        ? {
            ...node,
            allowedOperations:
              METADATA_TYPES.LOCATION_TYPE_CATEGORIES.allowedOperations,
          }
        : {
            ...node,
            allowedOperations: METADATA_TYPES.LOCATION_TYPES.allowedOperations,
          }

      dispatch(addLocationTypeOrLocationTypeCategoryNode(nodeToCreate))
    },
    [dispatch],
  )

  const handleDelete = useCallback(
    ({ id, type }) =>
      type === LOCATION_TYPE_NODE_TYPES.LOCATION_TYPE
        ? deleteLocationType(id)
        : deleteLocationTypeCategory(id),
    [deleteLocationType, deleteLocationTypeCategory],
  )

  const handleRevertReplaceLocationType = useCallback(
    ({ replaceToken }) => revertReplaceLocationType(replaceToken),
    [revertReplaceLocationType],
  )

  const handleSetIsCurationEnabled = useCallback(
    ({ id, isCurationEnabled, type }) =>
      setIsCurationEnabledLocationTypeOrLocationTypeCategory(
        id,
        isCurationEnabled,
        type,
      ),
    [setIsCurationEnabledLocationTypeOrLocationTypeCategory],
  )

  const handleRename = useCallback(
    ({ path, name }) =>
      dispatch(renameLocationTypeOrLocationTypeCategoryNode({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id, type }) =>
      dispatch(
        setDataForRenamingLocationTypeOrLocationTypeCategory({ id, type }),
      ),
    [dispatch],
  )

  const handleMove = useCallback(
    (nodesToMove, destinationNodes) => {
      const [destinationNode] = destinationNodes

      if (destinationNode.type === LOCATION_TYPE_NODE_TYPES.LOCATION_TYPE) {
        return showErrorNotification({
          title: 'Move Error',
          description: <div>Can&apos;t move to a location type</div>,
        })
      }

      const selectedLocationTypeCategoryNodes = filter(
        nodesToMove,
        ({ parentId }) => isNil(parentId),
      )

      if (!isEmpty(selectedLocationTypeCategoryNodes)) {
        return showErrorNotification({
          title: 'Move Error',
          description: (
            <div>
              Can&apos;t move location type categories:
              {map(selectedLocationTypeCategoryNodes, ({ name }) => (
                <Typography.Paragraph style={{ margin: 0 }} key={name}>
                  {name};
                </Typography.Paragraph>
              ))}
            </div>
          ),
        })
      }

      if (destinationNode.id === ROOT_METADATA_NODE_ID) {
        return showErrorNotification({
          title: 'Move Error',
          description: "Can't move location types to root",
        })
      }

      return dispatch(
        moveLocationTypes({
          nodesToMove,
          destinationNode,
        }),
      )
    },
    [dispatch],
  )

  const handleReplace = useCallback(
    async (nodesToReplace, replacementNodes) => {
      if (size(replacementNodes) > 1) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Can't Replace with more than 1 node",
        })
      }

      const [nodeToReplace] = nodesToReplace
      const [replacementNode] = replacementNodes

      if (
        nodeToReplace.type === LOCATION_TYPE_NODE_TYPES.LOCATION_TYPE_CATEGORY
      ) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Can't replace categories",
        })
      }

      if (
        replacementNode.type === LOCATION_TYPE_NODE_TYPES.LOCATION_TYPE_CATEGORY
      ) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "The node can't be replaced with a category",
        })
      }

      if (nodeToReplace.isCurationEnabled) {
        return showErrorNotification({
          title: 'Replace Error',
          description:
            "The node can't be replaced as it's still enabled for curation",
        })
      }

      if (!isEmpty(replacementNode.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't replace as it's already being replaced by ${replacementNode.replacedBy[0].name}`,
        })
      }

      if (!isEmpty(nodeToReplace.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's already being replaced by ${nodeToReplace.replacedBy[0].name}`,
        })
      }

      if (!isEmpty(nodeToReplace.replaces)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's replacing other nodes ${join(
            map(nodeToReplace.replaces, 'name'),
            ',',
          )}`,
        })
      }

      await replaceLocationType({
        idToReplace: nodeToReplace.id,
        replacementId: replacementNode.id,
      })

      return setIsReplaceActive(false)
    },
    [replaceLocationType],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetLocationTypesAndLocationTypeCategoriesTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveLocationTypesAndLocationTypeCategories(data),
    [data, saveLocationTypesAndLocationTypeCategories],
  )

  const showRenameInput = useCallback(
    (dataForRenaming, node) =>
      dataForRenaming?.id === node.id && dataForRenaming?.type === node.type,
    [],
  )

  const shouldPickSelectedSupportingItem = useCallback(
    (
      { id: mainNodeId, type: mainNodeType },
      { id: supportingNodeId, type: supportingNodeType },
    ) => mainNodeId === supportingNodeId && mainNodeType === supportingNodeType,
    [],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Location Type / Location Type Category error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  const { entities } = saveError || {}

  return isFetchLoading ||
    isSaveLoading ||
    isFetchLatestEventLoading ||
    isEmpty(treeData) ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Location Types</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />
              <HistoryLink
                renderEvent={renderLocationTypeEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={isMoveActive || isReplaceActive ? true : !isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      {!isEmpty(entities) && <MetadataTreeErrorsModal errors={entities} />}
      <ReduxMetadataTree
        disableLink={!hasPermissionToViewDetails}
        mainTreeExpandedKeys={mainTreeExpandedKeys}
        supportingTreeExpandedKeys={supportingTreeExpandedKeys}
        expandMainTree={expandLocationTypeAndLocationTypeCategoryMainTreeKeys}
        expandSupportingTree={
          expandLocationTypeAndLocationTypeCategorySupportingTreeKeys
        }
        metadataType={METADATA_TYPES.LOCATION_TYPE_CATEGORIES}
        secondaryMetadataType={METADATA_TYPES.LOCATION_TYPES}
        isDirty={isDirty}
        showSearch
        allowReplaceMany={false}
        treeData={treeData}
        editable={hasFullPermission}
        addNode={handleAdd}
        deleteNode={handleDelete}
        renameNode={handleRename}
        moveNodes={handleMove}
        replaceNode={handleReplace}
        revertReplaceNode={handleRevertReplaceLocationType}
        setDataForRenaming={handleSetDataForRenaming}
        setIsCurationEnabled={handleSetIsCurationEnabled}
        dataForRenaming={dataForRenaming}
        isMoveActive={isMoveActive}
        setIsMoveActive={setIsMoveActive}
        isReplaceActive={isReplaceActive}
        setIsReplaceActive={setIsReplaceActive}
        showRenameInput={showRenameInput}
        shouldPickSelectedSupportingItem={shouldPickSelectedSupportingItem}
      />
    </>
  )
}

export default LocationTypesPage
